import React, { useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { LANGS } from "../lib/translations";
import { Phone, Menu, X, Navigation } from "lucide-react";
import { BoatLogo } from "./BoatLogo";
import { PHONE_RAW, PHONE, MAPS_DIRECTIONS_URL } from "../lib/data";

const NAV_ITEMS = [
  { id: "about", key: "about" },
  { id: "fleet", key: "fleet" },
  { id: "reviews", key: "reviews" },
  { id: "contact", key: "contact" },
];

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-sky-100 shadow-[0_4px_24px_rgba(2,132,199,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 md:h-20 flex items-center justify-between gap-4">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2.5 group"
        >
          <span className="relative w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-500 via-sky-600 to-sky-700 grid place-items-center text-white shadow-[0_8px_22px_rgba(2,132,199,0.4)] group-hover:rotate-[-4deg] group-hover:-translate-y-0.5 transition-all duration-500 overflow-hidden">
            <BoatLogo className="w-7 h-7 relative z-10" strokeColor="#FACC15" />
            <span className="absolute -bottom-1 left-0 right-0 h-3 bg-white/15 rounded-full blur-md" />
          </span>
          <span className="font-heading text-[15px] md:text-base font-semibold tracking-tight text-slate-900 leading-tight">
            Pasohlávky
            <span className="block text-[10px] md:text-[11px] font-normal tracking-[0.18em] uppercase text-sky-600 -mt-0.5">
              Motorové čluny
            </span>
          </span>
        </a>

        <nav
          className="hidden lg:flex items-center gap-1"
          data-testid="desktop-nav"
        >
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => goTo(item.id)}
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-sky-700 rounded-full hover:bg-sky-50/80 transition-all"
            >
              {t.nav[item.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <div
            className="hidden sm:flex items-center bg-white/70 backdrop-blur border border-sky-100 rounded-full p-0.5 shadow-sm"
            data-testid="language-switcher"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-btn-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-2.5 py-1 text-[11px] font-semibold tracking-wider rounded-full transition-all ${
                  lang === l.code
                    ? "bg-slate-900 text-white shadow"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <a
            href={MAPS_DIRECTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-directions-cta"
            title={t.nav.directions}
            className="hidden md:inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/70 backdrop-blur border border-sky-100 text-sky-700 hover:text-sky-900 hover:bg-white transition-all"
          >
            <Navigation className="w-4 h-4" strokeWidth={2.5} />
          </a>
          <a
            href={`tel:${PHONE_RAW}`}
            data-testid="header-call-cta"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-4 py-2.5 text-sm font-semibold shadow-[0_8px_24px_rgba(250,204,21,0.45)] hover:-translate-y-0.5 transition-all"
          >
            <Phone className="w-4 h-4" strokeWidth={2.5} />
            <span>{PHONE}</span>
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((s) => !s)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full bg-white/70 backdrop-blur border border-sky-100 text-slate-700 hover:text-sky-700 transition"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-nav"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
          mobileOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-5 pb-6 pt-2 bg-white/95 backdrop-blur-xl border-b border-sky-100">
          <div className="flex flex-col gap-1 pt-2">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                data-testid={`mobile-nav-link-${item.id}`}
                onClick={() => goTo(item.id)}
                className="text-left px-4 py-3 rounded-2xl text-base font-medium text-slate-800 hover:bg-sky-50 hover:text-sky-700 transition"
              >
                {t.nav[item.key]}
              </button>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="flex items-center bg-sky-50 rounded-full p-0.5">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`mobile-lang-btn-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 text-xs font-semibold tracking-wider rounded-full transition ${
                    lang === l.code
                      ? "bg-slate-900 text-white"
                      : "text-slate-500"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <a
                href={MAPS_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-directions-cta"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-sky-50 text-sky-700 hover:bg-sky-100 transition"
                aria-label={t.nav.directions}
              >
                <Navigation className="w-4 h-4" strokeWidth={2.5} />
              </a>
              <a
                href={`tel:${PHONE_RAW}`}
                data-testid="mobile-call-cta"
                className="inline-flex items-center gap-2 rounded-full bg-yellow-400 text-slate-900 px-4 py-2.5 text-sm font-semibold shadow"
              >
                <Phone className="w-4 h-4" strokeWidth={2.5} />
                {t.nav.callNow}
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
